import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertActionLink,
  Button,
  Card,
  CardBody,
  CardExpandableContent,
  CardHeader,
  CardTitle,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  DescriptionListTermHelpText,
  DescriptionListTermHelpTextButton,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  MenuToggle,
  MenuToggleElement,
  Pagination,
  Popover,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr, TreeRowWrapper } from '@patternfly/react-table';
import { EyeIcon, GlobeIcon, SearchIcon } from '@patternfly/react-icons';
import type { DomainName } from '../../../types/models/DomainName';
import type { ZoneRecord } from '../../../types/models/Zone';
import type {
  ApiResponseActivateDnsZone,
  ApiResponseDnsRecordToggle,
  ApiResponseDnsRrsetToggle,
  DnsRrsetWriteResult,
} from '../../../types/api/ApiResponseDomainName';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useLocalStorageState } from '../../../utils/useLocalStorageState';
import {
  type DnsNameView,
  WRITABLE_RECORD_TYPES,
  buildRecordRows,
  displayRecordName,
  findSoaRecord,
  groupRecordsByType,
  isApexName,
  parseSoa,
  presentRecordTypes,
} from './zoneRecords';
import RecordSetFormModal from './RecordSetFormModal';
import RecordSetDeleteModal from './RecordSetDeleteModal';
import ActivateDnsZoneModal from './ActivateDnsZoneModal';
import DeactivateDnsZoneModal from './DeactivateDnsZoneModal';

const TYPE_FILTER_ALL_VALUE = 'all';
const DNS_NAME_VIEW_STORAGE_KEY = 'ancalagon-manager.dns.recordNameView';

/**
 * Sentinel per-page size meaning "show everything on one page". Any value safely larger than
 * the largest plausible record count works: PatternFly clamps the displayed range to
 * itemCount (so "of N" stays correct) and the row slice returns all rows. We do NOT track a
 * separate "show all" flag — selecting this value is the whole mechanism.
 */
const SHOW_ALL_PER_PAGE = 1_000_000;

/**
 * Per-page menu options. PatternFly's options menu renders each entry as
 * `${title} ${perPageSuffix}`, so to get "10 per page" … "100 per page" while the trailing
 * "All" reads just "All" (not "All per page"), we bake "per page" into the numeric titles and
 * blank the global suffix via DNS_PAGINATION_TITLES below. "All" is the last entry.
 */
const PER_PAGE_OPTIONS = [
  { title: '10 per page', value: 10 },
  { title: '20 per page', value: 20 },
  { title: '50 per page', value: 50 },
  { title: '100 per page', value: 100 },
  { title: 'All', value: SHOW_ALL_PER_PAGE },
];

/**
 * PatternFly's default Pagination titles with `perPageSuffix` cleared (see PER_PAGE_OPTIONS).
 * The `titles` prop replaces rather than merges PatternFly's defaults, so every field is
 * provided here to preserve the range "of" word and the navigation aria-labels.
 */
const DNS_PAGINATION_TITLES = {
  items: '',
  page: '',
  pages: '',
  itemsPerPage: 'Items per page',
  perPageSuffix: '',
  toFirstPageAriaLabel: 'Go to first page',
  toPreviousPageAriaLabel: 'Go to previous page',
  toLastPageAriaLabel: 'Go to last page',
  toNextPageAriaLabel: 'Go to next page',
  optionsToggleAriaLabel: '',
  currPageAriaLabel: 'Current page',
  paginationAriaLabel: 'Pagination',
  ofWord: 'of',
};

/** Stable per-row key — there is exactly one RRSet per (name, type). */
const rowKey = (record: ZoneRecord): string => `${record.name}|${record.type}`;

/**
 * A flattened tree-table row: either a type group (parent row carrying the chevron) or one
 * of that type's records (a leaf child). Records sit one level under their group, which is
 * expanded by default.
 */
type DnsDisplayRow =
  | { kind: 'group'; type: string; count: number; groupIndex: number }
  | { kind: 'record'; record: ZoneRecord; groupType: string; posInGroup: number };

interface SoaHelpTermProps {
  term: string;
  help: React.ReactNode;
}

/** A DescriptionList term that opens a Popover explaining the SOA/zone field on click. */
function SoaHelpTerm({ term, help }: SoaHelpTermProps) {
  return (
    <DescriptionListTermHelpText>
      <Popover headerContent={term} bodyContent={help}>
        <DescriptionListTermHelpTextButton>{term}</DescriptionListTermHelpTextButton>
      </Popover>
    </DescriptionListTermHelpText>
  );
}

interface DomainDnsZoneTabProps {
  domainName: DomainName;
  onRetry: () => Promise<void>;
}

function DomainDnsZoneTab({ domainName, onRetry }: DomainDnsZoneTabProps) {
  const { zone, zone_error: zoneError } = domainName;

  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const domainId = domainName.id;

  const [searchValue, setSearchValue] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [isTypeSelectOpen, setIsTypeSelectOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(25);
  const [isSoaExpanded, setIsSoaExpanded] = useState<boolean>(false);
  // Whether the "Activate DNS zone" modal is open (shown only from the unmanaged empty state).
  const [isActivateOpen, setIsActivateOpen] = useState(false);
  // Desync-resolution actions on the zone_error branch: the deactivate-confirm modal, and the
  // in-flight guard for the direct (non-confirmed) "Recreate zone" repair action.
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  const [repairing, setRepairing] = useState(false);
  // Record-name display view (relative/absolute), persisted in localStorage. A stale or
  // invalid stored value coerces back to 'relative'.
  const [storedNameView, setNameView] = useLocalStorageState<DnsNameView>(DNS_NAME_VIEW_STORAGE_KEY, 'relative');
  const nameView: DnsNameView = storedNameView === 'absolute' ? 'absolute' : 'relative';

  // Local copy of the zone records so a single-record enable/disable toggle can patch one
  // row from its response without refetching the whole domain (zones can be very large).
  const [records, setRecords] = useState<ZoneRecord[]>(zone?.records ?? []);
  // Per-row in-flight tracking so the Switch shows a pending (disabled) state mid-request.
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
  // Which type groups are expanded in the tree table. Seeded to ALL present types so every
  // group is open by default (re-defaulted below only when the set of present types changes).
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(
    () => new Set(presentRecordTypes(zone?.records ?? [])),
  );
  // Which record-set form modal (create/edit) is open, if any.
  const [formModal, setFormModal] = useState<{ mode: 'create' | 'edit'; record: ZoneRecord | null } | null>(null);
  const openCreate = () => setFormModal({ mode: 'create', record: null });
  const openEdit = (record: ZoneRecord) => setFormModal({ mode: 'edit', record });
  // Which record set (if any) is pending delete confirmation.
  const [deletingRecord, setDeletingRecord] = useState<ZoneRecord | null>(null);
  const openDelete = (record: ZoneRecord) => setDeletingRecord(record);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  // Re-sync local records only when the domain is genuinely refetched (Retry / fresh load).
  // domainName.zone's reference changes only on a real fetch, so local toggle-patches (which
  // do NOT refetch) are preserved between renders.
  useEffect(() => {
    setRecords(zone?.records ?? []);
  }, [zone]);

  const soaRecord = useMemo(() => findSoaRecord(records), [records]);
  const parsedSoa = useMemo(() => parseSoa(soaRecord), [soaRecord]);
  const availableTypes = useMemo(() => presentRecordTypes(records), [records]);
  const filteredRows = useMemo(
    () => buildRecordRows(records, domainName.name, typeFilter, debouncedSearch, nameView),
    [records, domainName.name, typeFilter, debouncedSearch, nameView],
  );
  // Flatten the filtered records into tree rows: each present type is a parent group
  // (its `count` is the FULL filtered total for that type), its records the leaf children.
  const displayRows = useMemo<DnsDisplayRow[]>(() => {
    const rows: DnsDisplayRow[] = [];
    groupRecordsByType(filteredRows).forEach((group, groupIndex) => {
      rows.push({ kind: 'group', type: group.type, count: group.records.length, groupIndex });
      group.records.forEach((record, i) => {
        rows.push({ kind: 'record', record, groupType: group.type, posInGroup: i + 1 });
      });
    });
    return rows;
  }, [filteredRows]);
  // A group's records are visible only while its group is expanded; group rows always show.
  const visibleRows = useMemo(
    () => displayRows.filter((r) => r.kind === 'group' || expandedTypes.has(r.groupType)),
    [displayRows, expandedTypes],
  );
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return visibleRows.slice(start, start + perPage);
  }, [visibleRows, page, perPage]);
  const itemCount = visibleRows.length;

  // Re-default expansion to "all groups open" only when the SET of present types changes
  // (a fresh zone / refetch) — not on a record patch like a toggle, which keeps the types.
  const availableTypesKey = availableTypes.join('|');
  useEffect(() => {
    setExpandedTypes(new Set(availableTypes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTypesKey]);

  // Keep the page in range as the visible-row count changes (filter / expand / collapse).
  useEffect(() => {
    setPage(1);
  }, [visibleRows.length]);

  const toggleType = useCallback((type: string) => {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const handleToggle = useCallback(
    async (record: ZoneRecord, nextChecked: boolean) => {
      // Switch semantics: checked = ENABLED. nextChecked true → enable; false → disable.
      if (domainName.id === null) {
        return; // defensive; the tab only renders for a loaded, managed domain
      }
      if (record.content.length !== 1) {
        return; // multi-content rows are not wired (Switch is disabled); guard anyway
      }
      const content = record.content[0];
      const action = nextChecked ? 'enablerecord' : 'disablerecord';
      const key = rowKey(record);

      setPendingKeys((prev) => new Set(prev).add(key));
      try {
        const response = await apiFetch(`/datacenters/domain-names/${domainName.id}/dns/${action}`, {
          method: 'POST',
          body: JSON.stringify({ name: record.name, type: record.type, content }),
        });

        let body: ApiResponseDnsRecordToggle | null;
        try {
          body = (await response.json()) as ApiResponseDnsRecordToggle;
        } catch {
          body = null;
        }

        if (!response.ok || !body || body.status !== 'success' || !body.data?.record) {
          const message =
            body?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim();
          throw new Error(message);
        }

        // Flip on success only: patch the one row's disabled from the echoed new state.
        const newDisabled = body.data.record.disabled;
        setRecords((prev) =>
          prev.map((r) => (r.name === record.name && r.type === record.type ? { ...r, disabled: newDisabled } : r)),
        );
        addNotification({
          title: newDisabled ? 'Record disabled' : 'Record enabled',
          srTitle: 'Success alert',
          description: `${record.type} ${record.name} ${newDisabled ? 'disabled' : 'enabled'}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
      } catch (err) {
        addNotification({
          title: 'Could not update record',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        // Do NOT flip: local state is unchanged, so the Switch returns to its prior position.
      } finally {
        setPendingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [apiFetch, addNotification, setDrawerOpen, domainName.id],
  );

  const handleRrsetToggle = useCallback(
    async (record: ZoneRecord, nextChecked: boolean) => {
      // Switch semantics: checked = ENABLED. nextChecked true → enable the whole set; false →
      // disable the whole set. Addresses the RRSet by (name, type) only — no per-record content.
      if (domainName.id === null) {
        return; // defensive; the tab only renders for a loaded, managed domain
      }
      const action = nextChecked ? 'enablerrset' : 'disablerrset';
      const key = rowKey(record);

      setPendingKeys((prev) => new Set(prev).add(key));
      try {
        const response = await apiFetch(`/datacenters/domain-names/${domainName.id}/dns/${action}`, {
          method: 'POST',
          body: JSON.stringify({ name: record.name, type: record.type }),
        });

        let body: ApiResponseDnsRrsetToggle | null;
        try {
          body = (await response.json()) as ApiResponseDnsRrsetToggle;
        } catch {
          body = null;
        }

        if (!response.ok || !body || body.status !== 'success' || !body.data?.rrset) {
          const message =
            body?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim();
          throw new Error(message);
        }

        // Flip on success only: patch the RRSet's disabled summary from the echoed new state.
        // Every record in this (name, type) set now shares that uniform state.
        const newDisabled = body.data.rrset.disabled;
        setRecords((prev) =>
          prev.map((r) => (r.name === record.name && r.type === record.type ? { ...r, disabled: newDisabled } : r)),
        );
        addNotification({
          title: newDisabled ? 'Record set disabled' : 'Record set enabled',
          srTitle: 'Success alert',
          description: `${record.type} ${record.name} record set ${newDisabled ? 'disabled' : 'enabled'}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
      } catch (err) {
        addNotification({
          title: 'Could not update record set',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        // Do NOT flip: local state is unchanged, so the Switch returns to its prior position.
      } finally {
        setPendingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [apiFetch, addNotification, setDrawerOpen, domainName.id],
  );

  const applyWrite = useCallback((original: ZoneRecord | null, result: DnsRrsetWriteResult) => {
    // One handler covers create (append), in-place edit (replace), and rename (drop old + insert
    // new). Drop BOTH the edited row's original identity AND any existing row at the result identity,
    // then append — buildRecordRows re-sorts, so order doesn't matter.
    setRecords((prev) => {
      const filtered = prev.filter((r) => {
        const isOriginal = original !== null && r.name === original.name && r.type === original.type;
        const isResultIdentity = r.name === result.name && r.type === result.type;
        return !isOriginal && !isResultIdentity;
      });
      return [...filtered, result];
    });
  }, []);

  const removeRecord = useCallback((record: ZoneRecord) => {
    setRecords((prev) => prev.filter((r) => !(r.name === record.name && r.type === record.type)));
  }, []);

  // Recreate a desynced zone on the bound endpoint (direct, non-destructive; adopts if it exists).
  // On success, refetch the domain — the reloaded entity drops zone_error and renders the records.
  const handleRepair = useCallback(async () => {
    if (domainId === null) return;
    setRepairing(true);
    try {
      const response = await apiFetch(`/datacenters/domain-names/${domainId}/zone/repair`, { method: 'POST' });
      let body: ApiResponseActivateDnsZone | null;
      try {
        body = (await response.json()) as ApiResponseActivateDnsZone;
      } catch {
        body = null;
      }
      if (!response.ok || !body || body.status !== 'success') {
        throw new Error(body?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim());
      }
      addNotification({
        title: 'DNS zone recreated',
        srTitle: 'Success alert',
        description: `The DNS zone for ${domainName.name} was recreated.`,
        variant: 'success',
      });
      setDrawerOpen(true);
      await onRetry();
    } catch (err) {
      addNotification({
        title: 'Could not recreate DNS zone',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setRepairing(false);
    }
  }, [apiFetch, addNotification, setDrawerOpen, onRetry, domainId, domainName.name]);

  // State 0 — domain not DNS-managed: offer to activate the zone. Must come BEFORE the zone_error /
  // zone === null branches: an unmanaged domain has zone === null and zone_error === null, so without
  // this it would fall through to the generic "No DNS zone data available" empty state.
  if (!domainName.is_managed) {
    return (
      <>
        <EmptyState titleText="DNS zone not active" icon={GlobeIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            This domain&apos;s DNS zone isn&apos;t hosted on a nameserver endpoint yet. Activate it to create the zone
            and manage its records here. Activating does not change anything at the registrar.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => setIsActivateOpen(true)} isDisabled={domainName.id === null}>
                Activate DNS zone
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
        <ActivateDnsZoneModal
          isOpen={isActivateOpen}
          onClose={() => setIsActivateOpen(false)}
          domainId={domainName.id ?? 0}
          apex={domainName.name}
          onActivated={onRetry}
        />
      </>
    );
  }

  // State 1 — the live zone read failed (zone_error set, zone null). Offer desync-resolution actions:
  // Retry (re-read), Recreate (direct, non-destructive repair), and Remove (confirmed deactivation).
  if (zoneError !== null) {
    return (
      <>
        <Alert
          variant="danger"
          isInline
          title="Failed to load DNS zone"
          actionLinks={
            <>
              <AlertActionLink onClick={() => void onRetry()}>Retry</AlertActionLink>
              <AlertActionLink
                onClick={() => {
                  if (!repairing && domainId !== null) void handleRepair();
                }}
                isDisabled={repairing}
              >
                {repairing ? 'Recreating zone…' : 'Recreate zone'}
              </AlertActionLink>
              <AlertActionLink onClick={() => setIsDeactivateOpen(true)}>Remove DNS zone activation</AlertActionLink>
            </>
          }
        >
          {zoneError}
        </Alert>
        <DeactivateDnsZoneModal
          isOpen={isDeactivateOpen}
          onClose={() => setIsDeactivateOpen(false)}
          domainId={domainId ?? 0}
          apex={domainName.name}
          onDeactivated={onRetry}
        />
      </>
    );
  }

  // State 3 — defensive: managed domain but neither a zone nor an error.
  if (zone === null) {
    return (
      <EmptyState titleText="No DNS zone data available" icon={GlobeIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>There is no DNS zone information to display for this domain.</EmptyStateBody>
      </EmptyState>
    );
  }

  // State 2 — zone present: SOA / zone card on top, records table below.
  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPage: number) => setPage(newPage);
  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };
  const clearSearch = () => {
    setSearchValue('');
    setDebouncedSearch('');
    setPage(1);
  };
  const clearAllFilters = () => {
    setSearchValue('');
    setDebouncedSearch('');
    setTypeFilter(null);
    setPage(1);
  };

  const hasNonSoaRecords = availableTypes.length > 0;
  const showClear = searchValue !== '' || typeFilter !== null;
  const typeToggleLabel = typeFilter ?? 'All types';

  const soaCard = (
    <Card id="soa-zone-card" isExpanded={isSoaExpanded} className="pf-v6-u-mb-md">
      <CardHeader
        onExpand={() => setIsSoaExpanded((prev) => !prev)}
        toggleButtonProps={{
          id: 'soa-zone-toggle',
          'aria-label': 'Toggle SOA / Zone details',
          'aria-labelledby': 'soa-zone-card-title soa-zone-toggle',
          'aria-expanded': isSoaExpanded,
        }}
      >
        <CardTitle id="soa-zone-card-title">SOA / Zone</CardTitle>
      </CardHeader>
      <CardExpandableContent>
        <CardBody>
          <DescriptionList columnModifier={{ default: '1Col', sm: '2Col', lg: '3Col' }}>
            <DescriptionListGroup>
              <DescriptionListTerm>Zone</DescriptionListTerm>
              <DescriptionListDescription>
                <span style={{ fontFamily: 'monospace' }}>{zone.name}</span>
              </DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <SoaHelpTerm
                term="Kind"
                help="The PowerDNS zone kind — Native (replicated via the database), Master (sends NOTIFY/AXFR to secondaries), or Slave (receives the zone from a primary)."
              />
              <DescriptionListDescription>{zone.kind}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <SoaHelpTerm
                term="Serial"
                help="The zone's current SOA serial. It increases on every change so secondary servers know to re-transfer the zone."
              />
              <DescriptionListDescription>{zone.serial}</DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <SoaHelpTerm term="DNSSEC" help="Whether DNSSEC signing is enabled for this zone." />
              <DescriptionListDescription>
                {zone.dnssec ? (
                  <Label color="green" isCompact>
                    Enabled
                  </Label>
                ) : (
                  <Label color="grey" isCompact>
                    Disabled
                  </Label>
                )}
              </DescriptionListDescription>
            </DescriptionListGroup>
            {parsedSoa !== null && (
              <>
                <DescriptionListGroup>
                  <SoaHelpTerm
                    term="Primary NS"
                    help="The primary authoritative nameserver for the zone (the SOA MNAME field)."
                  />
                  <DescriptionListDescription>
                    <span style={{ fontFamily: 'monospace' }}>{parsedSoa.primaryNs}</span>
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <SoaHelpTerm
                    term="Hostmaster"
                    help="The zone administrator's email in DNS form — the first dot stands for the @ sign (e.g. support.ancalagon.be. means support@ancalagon.be)."
                  />
                  <DescriptionListDescription>
                    <span style={{ fontFamily: 'monospace' }}>{parsedSoa.hostmaster}</span>
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <SoaHelpTerm
                    term="SOA serial"
                    help="The serial number from the SOA record (the same value as the zone serial). It must increase on each change to trigger zone transfers to secondaries."
                  />
                  <DescriptionListDescription>{parsedSoa.serial}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <SoaHelpTerm
                    term="Refresh"
                    help="How often (in seconds) a secondary nameserver checks the primary for zone updates."
                  />
                  <DescriptionListDescription>{parsedSoa.refresh}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <SoaHelpTerm
                    term="Retry"
                    help="How long (in seconds) a secondary waits before retrying after a failed refresh."
                  />
                  <DescriptionListDescription>{parsedSoa.retry}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <SoaHelpTerm
                    term="Expire"
                    help="How long (in seconds) a secondary keeps answering for the zone when it cannot reach the primary, before treating its copy as stale."
                  />
                  <DescriptionListDescription>{parsedSoa.expire}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <SoaHelpTerm
                    term="Negative TTL"
                    help="How long (in seconds) resolvers may cache negative answers, such as NXDOMAIN or a no-such-record response, for this zone."
                  />
                  <DescriptionListDescription>{parsedSoa.negativeTtl}</DescriptionListDescription>
                </DescriptionListGroup>
              </>
            )}
            {parsedSoa === null && soaRecord !== null && (
              <DescriptionListGroup>
                <DescriptionListTerm>SOA record</DescriptionListTerm>
                <DescriptionListDescription>
                  <span style={{ fontFamily: 'monospace' }}>{soaRecord.content[0] ?? '—'}</span>
                </DescriptionListDescription>
              </DescriptionListGroup>
            )}
          </DescriptionList>
        </CardBody>
      </CardExpandableContent>
    </Card>
  );

  const formModalEl =
    domainId !== null && formModal !== null ? (
      <RecordSetFormModal
        isOpen
        mode={formModal.mode}
        record={formModal.record}
        domainId={domainId}
        zoneApex={domainName.name}
        existingRecords={records}
        onClose={() => setFormModal(null)}
        onSuccess={(result, op) => {
          applyWrite(formModal.record, result);
          const created = op === 'created';
          addNotification({
            title: created ? 'Record set created' : 'Record set saved',
            srTitle: 'Success alert',
            description: `${result.type} ${displayRecordName(result.name, domainName.name, nameView)} ${
              created ? 'created' : 'saved'
            }.`,
            variant: 'success',
          });
          setDrawerOpen(true);
          setFormModal(null);
        }}
      />
    ) : null;

  const deleteModalEl =
    domainId !== null ? (
      <RecordSetDeleteModal
        record={deletingRecord}
        domainId={domainId}
        zoneApex={domainName.name}
        onClose={() => setDeletingRecord(null)}
        onEditInstead={() => {
          // Close the delete dialog and open the edit modal on the same set (remove a value, keep the rest).
          const target = deletingRecord;
          setDeletingRecord(null);
          if (target !== null) {
            openEdit(target);
          }
        }}
        onSuccess={() => {
          if (deletingRecord !== null) {
            removeRecord(deletingRecord);
            addNotification({
              title: 'Record set deleted',
              srTitle: 'Success alert',
              description: `${deletingRecord.type} ${displayRecordName(deletingRecord.name, domainName.name, nameView)} deleted.`,
              variant: 'success',
            });
            setDrawerOpen(true);
          }
          setDeletingRecord(null);
        }}
      />
    ) : null;

  if (!hasNonSoaRecords) {
    return (
      <>
        {soaCard}
        <EmptyState titleText="This zone has no records" icon={GlobeIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>This DNS zone has no records beyond its SOA.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={openCreate} isDisabled={domainId === null}>
                Create record set
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
        {formModalEl}
      </>
    );
  }

  const rowActions = (record: ZoneRecord): IAction[] => {
    const upperType = record.type.toUpperCase();
    const isApexNs = upperType === 'NS' && isApexName(record.name, domainName.name);
    const isWritable = WRITABLE_RECORD_TYPES.includes(upperType);

    // Apex NS is structural — the backend blocks create/edit/delete on it. Non-writable types are
    // not yet editable (deferred). isAriaDisabled (not isDisabled) keeps the item focusable so its
    // tooltip explanation shows on hover/focus.
    const editDisabledReason = isApexNs
      ? "The zone's apex NS record set can't be edited here."
      : !isWritable
        ? `Editing ${upperType} record sets isn't supported yet.`
        : null;
    const deleteDisabledReason = isApexNs
      ? "The zone's apex NS record set can't be deleted here."
      : !isWritable
        ? `Deleting ${upperType} record sets isn't supported yet.`
        : null;

    return [
      {
        title: 'Edit',
        onClick: () => openEdit(record),
        isAriaDisabled: editDisabledReason !== null || domainId === null,
        ...(editDisabledReason ? { tooltipProps: { content: editDisabledReason } } : {}),
      },
      {
        title: 'Delete',
        onClick: () => openDelete(record),
        isDanger: true,
        isAriaDisabled: deleteDisabledReason !== null || domainId === null,
        ...(deleteDisabledReason ? { tooltipProps: { content: deleteDisabledReason } } : {}),
      },
    ];
  };

  let tableBody: React.ReactNode;
  if (filteredRows.length === 0) {
    tableBody = (
      <EmptyState titleText="No records match your filters" icon={SearchIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>Try a different search term, change the type filter, or clear filters.</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={clearAllFilters}>
              Clear filters
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  } else {
    // Column sizing. The table uses auto layout, so one long unbroken value (a DKIM TXT record holds a
    // ~240-character base64 run) would set its minimum width and push Enabled and the row actions
    // off-screen, behind an easy-to-miss horizontal scrollbar. Content therefore breaks long tokens
    // anywhere (breakWord: PF's default overflow-wrap: break-word does not shrink a table column).
    // Content then takes nearly all the spare width, leaving Enabled at PF's header truncation floor
    // (5ch), which is narrower than its label: fitContent keeps "Enabled" whole. nowrap is a guard
    // that keeps group labels on one line.
    tableBody = (
      <Table isTreeTable aria-label={`DNS records for ${zone.name}`} variant="compact">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>TTL</Th>
            <Th>Content</Th>
            <Th modifier="fitContent">Enabled</Th>
            <Th screenReaderText="Row actions" />
          </Tr>
        </Thead>
        <Tbody>
          {paginatedRows.map((row, idx) => {
            if (row.kind === 'group') {
              const isExpanded = expandedTypes.has(row.type);
              // aria-setsize = child count; PatternFly renders the expand toggle only when > 0.
              const groupProps = {
                isExpanded,
                isHidden: false,
                'aria-level': 1,
                'aria-posinset': row.groupIndex + 1,
                'aria-setsize': row.count,
                toggleAriaLabel: `Toggle ${row.type} records`,
              };
              return (
                <TreeRowWrapper
                  key={`group-${row.type}`}
                  row={{ props: groupProps }}
                  className="ancalagon-dns-group-header"
                >
                  <Td
                    dataLabel="Name"
                    modifier="nowrap"
                    treeRow={{
                      onCollapse: () => toggleType(row.type),
                      props: groupProps,
                      rowIndex: idx,
                    }}
                  >
                    {row.type}{' '}
                    <span className="pf-v6-u-font-size-sm">
                      ({row.count} {row.count === 1 ? 'record' : 'records'})
                    </span>
                  </Td>
                  <Td dataLabel="TTL" />
                  <Td dataLabel="Content" />
                  <Td dataLabel="Enabled" />
                  <Td />
                </TreeRowWrapper>
              );
            }

            const record = row.record;
            const upperType = record.type.toUpperCase();
            const isSingle = record.content.length === 1;
            const isPending = pendingKeys.has(rowKey(record));
            // The apex NS RRSet may be ENABLED but never DISABLED — disabling the zone's own
            // delegation at the root breaks it (the backend rejects this). When the apex NS set is
            // currently enabled, lock the Switch on. Applies whether it is single- or multi-valued.
            const isApexNs = upperType === 'NS' && isApexName(record.name, domainName.name);
            const apexNsLockOn = isApexNs && !record.disabled;
            const toggle = (
              <Switch
                id={`dns-record-toggle-${rowKey(record)}`}
                aria-label={`Enable ${upperType} ${isSingle ? 'record' : 'record set'} ${record.name}`}
                isChecked={!record.disabled}
                hasCheckIcon
                isDisabled={isPending || apexNsLockOn}
                onChange={(_event, checked) => {
                  // Single-value RRSets keep using the precise per-record endpoint (pinned by
                  // content). Multi-value (round-robin) sets toggle all-or-nothing via the
                  // whole-RRSet endpoint.
                  if (isSingle) {
                    void handleToggle(record, checked);
                  } else {
                    void handleRrsetToggle(record, checked);
                  }
                }}
              />
            );
            // Leaf row: aria-setsize 0 → no toggle, indented one level under its group.
            const recordProps = {
              isExpanded: false,
              isHidden: false,
              'aria-level': 2,
              'aria-posinset': row.posInGroup,
              'aria-setsize': 0,
            };
            return (
              <TreeRowWrapper key={rowKey(record)} row={{ props: recordProps }}>
                <Td
                  dataLabel="Name"
                  treeRow={{
                    onCollapse: () => undefined,
                    props: recordProps,
                    rowIndex: idx,
                  }}
                >
                  <span style={{ fontFamily: 'monospace' }}>
                    {displayRecordName(record.name, domainName.name, nameView)}
                  </span>
                </Td>
                <Td dataLabel="TTL">{record.ttl}</Td>
                <Td dataLabel="Content" modifier="breakWord">
                  {record.content.length === 0 ? (
                    '—'
                  ) : (
                    <div style={{ whiteSpace: 'pre-line', fontFamily: 'monospace' }}>{record.content.join('\n')}</div>
                  )}
                </Td>
                <Td dataLabel="Enabled">
                  {apexNsLockOn ? (
                    <Tooltip content="The zone's apex NS records cannot be disabled.">
                      <span>{toggle}</span>
                    </Tooltip>
                  ) : (
                    toggle
                  )}
                </Td>
                <Td isActionCell>
                  <ActionsColumn items={rowActions(record)} />
                </Td>
              </TreeRowWrapper>
            );
          })}
        </Tbody>
      </Table>
    );
  }

  return (
    <>
      {soaCard}
      <Toolbar id="dns-records-toolbar">
        <ToolbarContent>
          <ToolbarItem>
            <Button variant="primary" onClick={openCreate} isDisabled={domainId === null}>
              Create record set
            </Button>
          </ToolbarItem>
          <ToolbarItem>
            <Popover
              headerContent="Record name display"
              bodyContent={
                <>
                  <Content component="p" className="pf-v6-u-mb-sm">
                    Relative hides the zone name and shows @ for the apex. Absolute shows the full domain name.
                  </Content>
                  <ToggleGroup aria-label="Record name display">
                    <ToggleGroupItem
                      text="Relative"
                      isSelected={nameView === 'relative'}
                      onChange={() => setNameView('relative')}
                    />
                    <ToggleGroupItem
                      text="Absolute"
                      isSelected={nameView === 'absolute'}
                      onChange={() => setNameView('absolute')}
                    />
                  </ToggleGroup>
                </>
              }
            >
              <Button variant="plain" aria-label="Display options" icon={<EyeIcon />} />
            </Popover>
          </ToolbarItem>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by name or value..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={clearSearch}
              aria-label="Search DNS records"
            />
          </ToolbarItem>
          <ToolbarItem>
            <Select
              isOpen={isTypeSelectOpen}
              selected={typeFilter ?? TYPE_FILTER_ALL_VALUE}
              onOpenChange={(open) => setIsTypeSelectOpen(open)}
              onSelect={(_event, value) => {
                setIsTypeSelectOpen(false);
                if (value === TYPE_FILTER_ALL_VALUE) {
                  setTypeFilter(null);
                } else if (typeof value === 'string') {
                  setTypeFilter(value);
                }
                setPage(1);
              }}
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsTypeSelectOpen((open) => !open)}
                  isExpanded={isTypeSelectOpen}
                  aria-label="Filter records by type"
                  style={{ minWidth: '180px' }}
                >
                  {typeToggleLabel}
                </MenuToggle>
              )}
              shouldFocusToggleOnSelect
            >
              <SelectList>
                <SelectOption value={TYPE_FILTER_ALL_VALUE}>All types</SelectOption>
                {availableTypes.map((type) => (
                  <SelectOption key={type} value={type}>
                    {type}
                  </SelectOption>
                ))}
              </SelectList>
            </Select>
          </ToolbarItem>
          {showClear && (
            <ToolbarItem>
              <Button variant="link" onClick={clearAllFilters}>
                Clear
              </Button>
            </ToolbarItem>
          )}
          <ToolbarGroup align={{ default: 'alignEnd' }}>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={itemCount}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
                perPageOptions={PER_PAGE_OPTIONS}
                titles={DNS_PAGINATION_TITLES}
                variant="top"
                isCompact
              />
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>

      {tableBody}

      {itemCount > 0 && (
        <Pagination
          itemCount={itemCount}
          perPage={perPage}
          page={page}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          perPageOptions={PER_PAGE_OPTIONS}
          titles={DNS_PAGINATION_TITLES}
          variant="bottom"
        />
      )}
      {deleteModalEl}
      {formModalEl}
    </>
  );
}

export default DomainDnsZoneTab;
